import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Fragment, useEffect, useRef, useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import Alert from "react-bootstrap/Alert";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Nav from "react-bootstrap/Nav";
import Row from "react-bootstrap/Row";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import { useNavigate } from "react-router-dom";
import {
  API_BASE,
  adminBulkCatalogItems,
  adminImportCatalog,
  adminAddCatalogSubcategory,
  adminCreateCatalogCategory,
  adminCreateCatalogItem,
  adminCreateUser,
  adminDeleteCatalogCategory,
  adminDeleteCatalogSubcategory,
  adminDeleteCatalogItem,
  adminRenameCatalogSubcategory,
  adminDeleteUser,
  adminFetchAllCatalogItems,
  adminFetchCatalogCategories,
  adminSyncContent,
  adminUpdateCatalogCategory,
  adminUpdateCatalogItem,
  adminUpdateUser,
  adminUploadCatalogImage,
  deleteAccount,
  fetchAllUsers,
  fetchPageContent,
  updateProfile,
} from "../api.js";
import {
  imageDestination,
  imageExtFromName,
  imageExtFromUrl,
  sanitizeItemFields,
  splitDescription,
} from "../lib/catalogSanitize.js";
import { useAuth } from "../context/AuthContext.jsx";

const ROLE_LABELS = { admin: "Admin", support: "Support", user: "User" };
const ROLE_VARIANTS = { admin: "danger", support: "warning", user: "secondary" };

function Initials({ name }) {
  const parts = (name || "?").trim().split(/\s+/);
  const letters = parts.length >= 2
    ? parts[0][0] + parts[parts.length - 1][0]
    : parts[0].slice(0, 2);
  return (
    <div
      className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
      style={{ width: 80, height: 80, fontSize: 28, background: "var(--bs-primary)" }}
    >
      {letters.toUpperCase()}
    </div>
  );
}

// ── General section ───────────────────────────────────────────────────────────
function GeneralSection() {
  const { user, token, updateUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name ?? "", companyName: user?.companyName ?? "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const updated = await updateProfile(token, { name: form.name, companyName: form.companyName });
      updateUser(updated);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h5 className="fw-semibold mb-4">General</h5>
      <div className="d-flex align-items-center gap-3 mb-4">
        <Initials name={user?.name} />
        <div>
          <div className="fw-semibold fs-5">{user?.name}</div>
          <div className="text-muted small">{user?.email}</div>
          <Badge bg={ROLE_VARIANTS[user?.role] ?? "secondary"} className="mt-1">
            {ROLE_LABELS[user?.role] ?? user?.role}
          </Badge>
        </div>
      </div>
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(false)}>Profile updated.</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={handleSave}>
        <Form.Group className="mb-3">
          <Form.Label>Full Name</Form.Label>
          <Form.Control type="text" value={form.name} onChange={set("name")} required />
        </Form.Group>
        <Form.Group className="mb-4">
          <Form.Label>Company Name</Form.Label>
          <Form.Control type="text" value={form.companyName} onChange={set("companyName")} required />
        </Form.Group>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving && <Spinner animation="border" size="sm" className="me-2" />}
          Save Changes
        </Button>
      </Form>
    </div>
  );
}

// ── Account section ───────────────────────────────────────────────────────────
function AccountSection() {
  const { user, token, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: user?.email ?? "", password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const payload = { email: form.email };
      if (form.password) payload.password = form.password;
      const updated = await updateProfile(token, payload);
      updateUser(updated);
      setForm((f) => ({ ...f, password: "" }));
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteAccount(token);
      logout();
      navigate("/");
    } catch (err) {
      setDeleteError(err.message);
      setDeleting(false);
    }
  }

  return (
    <div>
      <h5 className="fw-semibold mb-4">Account</h5>
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(false)}>Account updated.</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={handleSave}>
        <Form.Group className="mb-3">
          <Form.Label>Email Address</Form.Label>
          <Form.Control type="email" value={form.email} onChange={set("email")} required />
        </Form.Group>
        <Form.Group className="mb-4">
          <Form.Label>New Password</Form.Label>
          <Form.Control
            type="password"
            placeholder="Leave blank to keep current password"
            value={form.password}
            onChange={set("password")}
            minLength={form.password ? 8 : undefined}
          />
        </Form.Group>
        <Button type="submit" variant="primary" disabled={saving}>
          {saving && <Spinner animation="border" size="sm" className="me-2" />}
          Save Changes
        </Button>
      </Form>

      <hr className="my-4" />
      <h6 className="text-danger mb-2">Danger Zone</h6>
      <p className="text-muted small mb-3">Permanently delete your account and all associated data.</p>
      <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete Account</Button>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton><Modal.Title>Delete Account</Modal.Title></Modal.Header>
        <Modal.Body>
          {deleteError && <Alert variant="danger">{deleteError}</Alert>}
          Are you sure you want to delete your account? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting && <Spinner animation="border" size="sm" className="me-2" />}
            Delete Account
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ── Notifications section ─────────────────────────────────────────────────────
function NotificationsSection() {
  const { user, token, updateUser } = useAuth();
  const [settings, setSettings] = useState({
    newsletter: user?.settings?.newsletter ?? false,
    product_update: user?.settings?.product_update ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function handleToggle(key) {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const updated = await updateProfile(token, { settings: next });
      updateUser(updated);
      setSuccess(true);
    } catch (err) {
      setSettings(settings);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h5 className="fw-semibold mb-1">Notifications</h5>
      <p className="text-muted small mb-4">Choose which emails you'd like to receive.</p>
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(false)}>Preferences saved.</Alert>}
      {error && <Alert variant="danger">{error}</Alert>}
      {saving && <div className="text-muted small mb-2"><Spinner animation="border" size="sm" className="me-1" />Saving…</div>}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <div className="fw-semibold">Newsletter</div>
            <div className="text-muted small">Receive our periodic newsletter with updates and offers.</div>
          </div>
          <Form.Check
            type="switch"
            id="toggle-newsletter"
            checked={settings.newsletter}
            onChange={() => handleToggle("newsletter")}
            disabled={saving}
          />
        </Card.Body>
      </Card>
      <Card className="border-0 shadow-sm">
        <Card.Body className="d-flex justify-content-between align-items-center">
          <div>
            <div className="fw-semibold">Product Updates</div>
            <div className="text-muted small">Get notified when products you follow are updated.</div>
          </div>
          <Form.Check
            type="switch"
            id="toggle-product-update"
            checked={settings.product_update}
            onChange={() => handleToggle("product_update")}
            disabled={saving}
          />
        </Card.Body>
      </Card>
    </div>
  );
}

// ── Admin section ─────────────────────────────────────────────────────────────
function AdminSection() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    setError(null);
    try {
      setUsers(await fetchAllUsers(token));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleCreated(newUser) {
    setUsers((prev) => [newUser, ...prev]);
    setShowCreate(false);
  }

  function handleUpdated(updated) {
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    setEditTarget(null);
  }

  async function handleDelete() {
    try {
      await adminDeleteUser(token, deleteTarget.id);
      setUsers((prev) => prev.filter((u) => u.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      alert(err.message);
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="fw-semibold mb-0">User Management</h5>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>+ Create User</Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="py-4 text-center"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <div className="table-responsive">
          <Table hover className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Company</th>
                <th>Role</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td className="fw-semibold">{u.name}</td>
                  <td className="text-muted small">{u.email}</td>
                  <td className="text-muted small">{u.companyName}</td>
                  <td>
                    <Badge bg={ROLE_VARIANTS[u.role] ?? "secondary"}>
                      {ROLE_LABELS[u.role] ?? u.role}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={u.active ? "success" : "secondary"}>
                      {u.active ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="text-end">
                    <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => setEditTarget(u)}>Edit</Button>
                    <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(u)}>Delete</Button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="text-center text-muted py-4">No users found.</td></tr>
              )}
            </tbody>
          </Table>
        </div>
      )}

      <CreateUserModal show={showCreate} onHide={() => setShowCreate(false)} onCreated={handleCreated} token={token} />
      <EditUserModal show={!!editTarget} target={editTarget} onHide={() => setEditTarget(null)} onUpdated={handleUpdated} token={token} />

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Delete User</Modal.Title></Modal.Header>
        <Modal.Body>
          Delete <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email})? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function CreateUserModal({ show, onHide, onCreated, token }) {
  const [form, setForm] = useState({ name: "", email: "", companyName: "", password: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await adminCreateUser(token, form);
      onCreated(created);
      setForm({ name: "", email: "", companyName: "", password: "", role: "user" });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Create User</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Full Name</Form.Label>
            <Form.Control type="text" value={form.name} onChange={set("name")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" value={form.email} onChange={set("email")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Company Name</Form.Label>
            <Form.Control type="text" value={form.companyName} onChange={set("companyName")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Password</Form.Label>
            <Form.Control type="password" value={form.password} onChange={set("password")} required minLength={8} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select value={form.role} onChange={set("role")}>
              <option value="user">User</option>
              <option value="support">Support</option>
              <option value="admin">Admin</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Create
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function EditUserModal({ show, target, onHide, onUpdated, token }) {
  const [form, setForm] = useState({ name: "", email: "", companyName: "", role: "user", active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (target) {
      setForm({
        name: target.name ?? "",
        email: target.email ?? "",
        companyName: target.companyName ?? "",
        role: target.role ?? "user",
        active: target.active ?? true,
      });
      setError(null);
    }
  }, [target]);

  function set(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const updated = await adminUpdateUser(token, target.id, form);
      onUpdated(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Edit User</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Full Name</Form.Label>
            <Form.Control type="text" value={form.name} onChange={set("name")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control type="email" value={form.email} onChange={set("email")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Company Name</Form.Label>
            <Form.Control type="text" value={form.companyName} onChange={set("companyName")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Role</Form.Label>
            <Form.Select value={form.role} onChange={set("role")}>
              <option value="user">User</option>
              <option value="support">Support</option>
              <option value="admin">Admin</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select value={form.active ? "true" : "false"} onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === "true" }))}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ── Content section ───────────────────────────────────────────────────────────

function tf(val) {
  return val ?? "";
}

function HeroEditor({ hero, onChange }) {
  function set(field) {
    return (e) => onChange({ ...hero, [field]: e.target.value });
  }
  return (
    <div className="mb-4">
      <h6 className="fw-semibold text-muted text-uppercase small mb-3">Hero</h6>
      <Form.Group className="mb-2">
        <Form.Label className="small">Kicker</Form.Label>
        <Form.Control size="sm" value={tf(hero?.kicker)} onChange={set("kicker")} />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small">Headline</Form.Label>
        <Form.Control size="sm" value={tf(hero?.headline)} onChange={set("headline")} />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label className="small">Subheadline</Form.Label>
        <Form.Control as="textarea" rows={2} size="sm" value={tf(hero?.subheadline)} onChange={set("subheadline")} />
      </Form.Group>
      <Row>
        <Col>
          <Form.Group className="mb-2">
            <Form.Label className="small">CTA Label</Form.Label>
            <Form.Control size="sm" value={tf(hero?.primaryCtaLabel)} onChange={set("primaryCtaLabel")} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mb-2">
            <Form.Label className="small">CTA Link</Form.Label>
            <Form.Control size="sm" value={tf(hero?.primaryCtaHref)} onChange={set("primaryCtaHref")} />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
}

function ArrayListEditor({ items, onChange, fields, addLabel, emptyItem }) {
  function updateItem(idx, field, val) {
    const next = items.map((item, i) => i === idx ? { ...item, [field]: val } : item);
    onChange(next);
  }
  function removeItem(idx) {
    onChange(items.filter((_, i) => i !== idx));
  }
  function addItem() {
    onChange([...items, { ...emptyItem }]);
  }
  return (
    <div>
      {items.map((item, idx) => (
        <div key={idx} className="border rounded p-2 mb-2 bg-light">
          <div className="d-flex justify-content-between align-items-start mb-1">
            <span className="small text-muted fw-semibold">#{idx + 1}</span>
            <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => removeItem(idx)}>Remove</Button>
          </div>
          {fields.map(({ key, label, textarea }) => (
            <Form.Group key={key} className="mb-1">
              <Form.Label className="small mb-0">{label}</Form.Label>
              {textarea
                ? <Form.Control as="textarea" rows={2} size="sm" value={tf(item[key])} onChange={(e) => updateItem(idx, key, e.target.value)} />
                : <Form.Control size="sm" value={tf(item[key])} onChange={(e) => updateItem(idx, key, e.target.value)} />
              }
            </Form.Group>
          ))}
        </div>
      ))}
      <Button variant="outline-secondary" size="sm" onClick={addItem}>+ {addLabel}</Button>
    </div>
  );
}

function BulletsEditor({ bullets, onChange }) {
  return (
    <ArrayListEditor
      items={(bullets ?? []).map((b) => ({ text: b }))}
      onChange={(arr) => onChange(arr.map((i) => i.text))}
      fields={[{ key: "text", label: "Bullet text" }]}
      addLabel="Add bullet"
      emptyItem={{ text: "" }}
    />
  );
}

function SectionEditor({ section, onChange }) {
  function set(field) {
    return (e) => onChange({ ...section, [field]: e.target.value });
  }
  const layout = section.layout;
  return (
    <div>
      <Row className="mb-2">
        <Col>
          <Form.Group>
            <Form.Label className="small">Kicker</Form.Label>
            <Form.Control size="sm" value={tf(section.kicker)} onChange={set("kicker")} />
          </Form.Group>
        </Col>
        <Col>
          <Form.Group>
            <Form.Label className="small">Title</Form.Label>
            <Form.Control size="sm" value={tf(section.title)} onChange={set("title")} />
          </Form.Group>
        </Col>
      </Row>

      {(layout === "split" || layout === "overlap") && (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small">Body</Form.Label>
            <Form.Control as="textarea" rows={3} size="sm" value={tf(section.body)} onChange={set("body")} />
          </Form.Group>
          <Row className="mb-2">
            <Col>
              <Form.Group>
                <Form.Label className="small">Media Side</Form.Label>
                <Form.Select size="sm" value={section.mediaSide ?? "end"} onChange={set("mediaSide")}>
                  <option value="end">Right</option>
                  <option value="start">Left</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label className="small">Background</Form.Label>
                <Form.Select
                  size="sm"
                  value={section.background?.type ?? "gradient"}
                  onChange={(e) => onChange({ ...section, background: { ...(section.background ?? {}), type: e.target.value } })}
                >
                  <option value="gradient">Gradient</option>
                  <option value="image">Image</option>
                  <option value="light">Light</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Group>
                <Form.Label className="small">Image URL</Form.Label>
                <Form.Control size="sm" placeholder="https://..." value={tf(section.imageUrl)} onChange={set("imageUrl")} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label className="small">Image Alt</Form.Label>
                <Form.Control size="sm" value={tf(section.imageAlt)} onChange={set("imageAlt")} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-2">
            <Form.Label className="small">Bullets</Form.Label>
            <BulletsEditor bullets={section.bullets ?? []} onChange={(b) => onChange({ ...section, bullets: b })} />
          </Form.Group>
          <Row className="mb-2">
            <Col>
              <Form.Group>
                <Form.Label className="small">CTA Label</Form.Label>
                <Form.Control size="sm" value={tf(section.ctaLabel)} onChange={set("ctaLabel")} />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label className="small">CTA URL</Form.Label>
                <Form.Control size="sm" value={tf(section.ctaHref)} onChange={set("ctaHref")} />
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      {layout === "stack" && (
        <Form.Group className="mb-2">
          <Form.Label className="small">Items</Form.Label>
          <ArrayListEditor
            items={section.items ?? []}
            onChange={(items) => onChange({ ...section, items })}
            fields={[
              { key: "title", label: "Title" },
              { key: "body", label: "Body", textarea: true },
            ]}
            addLabel="Add item"
            emptyItem={{ title: "", body: "" }}
          />
        </Form.Group>
      )}

      {layout === "carousel" && (
        <>
          <Form.Group className="mb-2">
            <Form.Label className="small">Slides</Form.Label>
            <ArrayListEditor
              items={section.slides ?? []}
              onChange={(slides) => onChange({ ...section, slides })}
              fields={[
                { key: "title", label: "Title" },
                { key: "body", label: "Body", textarea: true },
                { key: "imageUrl", label: "Image URL" },
                { key: "imageAlt", label: "Image Alt" },
              ]}
              addLabel="Add slide"
              emptyItem={{ title: "", body: "", imageUrl: "", imageAlt: "" }}
            />
          </Form.Group>
          <Form.Check
            type="checkbox"
            id={`carousel-loop-${section.id ?? "x"}`}
            label="Enable loop (wrap from last slide back to first)"
            checked={section.loop !== false}
            onChange={(e) => onChange({ ...section, loop: e.target.checked })}
            className="mb-2 small"
          />
        </>
      )}

      {layout === "collage" && (
        <Form.Group className="mb-2">
          <Form.Label className="small">Tiles</Form.Label>
          <ArrayListEditor
            items={section.tiles ?? []}
            onChange={(tiles) => onChange({ ...section, tiles })}
            fields={[
              { key: "caption", label: "Caption" },
              { key: "imageUrl", label: "Image URL" },
              { key: "imageAlt", label: "Image Alt" },
            ]}
            addLabel="Add tile"
            emptyItem={{ caption: "", imageUrl: "", imageAlt: "", colMd: 6 }}
          />
        </Form.Group>
      )}

      {layout === "infocards" && (
        <Form.Group className="mb-2">
          <Form.Label className="small">Cards</Form.Label>
          <ArrayListEditor
            items={section.cards ?? []}
            onChange={(cards) => onChange({ ...section, cards })}
            fields={[
              { key: "title", label: "Title" },
              { key: "body", label: "Body", textarea: true },
            ]}
            addLabel="Add card"
            emptyItem={{ title: "", body: "", iconKey: "boxes" }}
          />
        </Form.Group>
      )}
    </div>
  );
}


function SortableSection({ id, section, idx, onChange, onRemove, onToggleHide }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : section.hidden ? 0.55 : 1,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <Accordion.Item eventKey={String(idx)}>
        <Accordion.Header>
          <span
            {...attributes}
            {...listeners}
            className="me-2 text-muted"
            style={{ cursor: "grab", fontSize: "1.1rem", lineHeight: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            ⠿
          </span>
          <span className="small flex-grow-1" style={{ minWidth: 0 }}>
            <Badge bg="secondary" className="me-2 text-lowercase">{section.layout}</Badge>
            {section.title || section.id || `Section ${idx + 1}`}
            {section.hidden && (
              <Badge bg="warning" text="dark" className="ms-2 text-lowercase">hidden</Badge>
            )}
          </span>
          <span className="d-flex gap-1 me-2" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm" variant="link" className="p-0 text-muted"
              title={section.hidden ? "Show section" : "Hide section"}
              onClick={onToggleHide}
            >
              <i className={`bi ${section.hidden ? "bi-eye-slash" : "bi-eye"}`} />
            </Button>
            <Button
              size="sm" variant="link" className="p-0 text-danger"
              title="Remove section"
              onClick={onRemove}
            >
              <i className="bi bi-trash" />
            </Button>
          </span>
        </Accordion.Header>
        <Accordion.Body>
          <SectionEditor section={section} onChange={onChange} />
        </Accordion.Body>
      </Accordion.Item>
    </div>
  );
}

function PageContentEditor({ slug, token, onSaved }) {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionLayout, setNewSectionLayout] = useState("overlap");
  const [newSectionTitle, setNewSectionTitle] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPageContent(slug)
      .then((d) => { if (!cancelled) setPageData(d); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  function updateHero(hero) {
    setPageData((p) => ({ ...p, hero }));
  }

  function updateSection(idx, updatedSection) {
    setPageData((p) => {
      const sections = p.sections.map((s, i) => i === idx ? updatedSection : s);
      return { ...p, sections };
    });
  }

  function removeSection(idx) {
    setPageData((p) => ({ ...p, sections: p.sections.filter((_, i) => i !== idx) }));
  }

  function toggleHideSection(idx) {
    setPageData((p) => ({
      ...p,
      sections: p.sections.map((s, i) => i === idx ? { ...s, hidden: !s.hidden } : s),
    }));
  }

  function handleAddSection() {
    const defaults = {
      overlap:   { background: { type: "gradient" }, mediaSide: "end" },
      stack:     { items: [] },
      carousel:  { slides: [] },
      collage:   { tiles: [], variant: "cards" },
      infocards: { cards: [] },
    };
    const newSection = {
      id: `section-${Date.now()}`,
      layout: newSectionLayout,
      title: newSectionTitle.trim(),
      ...(defaults[newSectionLayout] ?? {}),
    };
    setPageData((p) => ({ ...p, sections: [...(p.sections ?? []), newSection] }));
    setNewSectionTitle("");
    setShowAddSection(false);
  }

  function reorderSections(activeId, overId) {
    setPageData((p) => {
      const ids = p.sections.map((s, i) => s.id ?? String(i));
      const oldIndex = ids.indexOf(activeId);
      const newIndex = ids.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return p;
      return { ...p, sections: arrayMove(p.sections, oldIndex, newIndex) };
    });
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const { site, id, ...page } = pageData;
      await adminSyncContent(token, page);
      if (onSaved) onSaved();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="py-3 text-center"><Spinner animation="border" size="sm" /></div>;
  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!pageData) return null;

  return (
    <div>
      {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>{saveError}</Alert>}

      {pageData.hero !== undefined && (
        <HeroEditor hero={pageData.hero} onChange={updateHero} />
      )}

      {(pageData.sections?.length > 0 || true) && (
        <div className="mb-4">
          <h6 className="fw-semibold text-muted text-uppercase small mb-3">
            Sections{" "}
            <span className="fw-normal text-muted" style={{ fontSize: "0.7rem" }}>(drag ⠿ to reorder)</span>
          </h6>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => { if (over) reorderSections(String(active.id), String(over.id)); }}
          >
            <SortableContext
              items={(pageData.sections ?? []).map((s, i) => s.id ?? String(i))}
              strategy={verticalListSortingStrategy}
            >
              <Accordion>
                {(pageData.sections ?? []).map((section, idx) => (
                  <SortableSection
                    key={section.id ?? idx}
                    id={section.id ?? String(idx)}
                    section={section}
                    idx={idx}
                    onChange={(updated) => updateSection(idx, updated)}
                    onRemove={() => removeSection(idx)}
                    onToggleHide={() => toggleHideSection(idx)}
                  />
                ))}
              </Accordion>
            </SortableContext>
          </DndContext>

          {showAddSection ? (
            <div className="mt-2 p-3 border rounded bg-light">
              <Row className="g-2 align-items-end">
                <Col>
                  <Form.Label className="small mb-1">Layout</Form.Label>
                  <Form.Select size="sm" value={newSectionLayout} onChange={(e) => setNewSectionLayout(e.target.value)}>
                    <option value="overlap">Overlap</option>
                    <option value="stack">Stack</option>
                    <option value="carousel">Carousel</option>
                    <option value="collage">Collage</option>
                    <option value="infocards">Capability Cards</option>
                  </Form.Select>
                </Col>
                <Col>
                  <Form.Label className="small mb-1">Title</Form.Label>
                  <Form.Control
                    size="sm"
                    placeholder="Section title…"
                    value={newSectionTitle}
                    onChange={(e) => setNewSectionTitle(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddSection()}
                  />
                </Col>
                <Col xs="auto">
                  <Button size="sm" variant="primary" onClick={handleAddSection}>Add</Button>
                  <Button size="sm" variant="link" className="ms-1 text-muted"
                    onClick={() => { setShowAddSection(false); setNewSectionTitle(""); }}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </div>
          ) : (
            <Button size="sm" variant="outline-secondary" className="mt-2"
              onClick={() => setShowAddSection(true)}>
              + Add section
            </Button>
          )}
        </div>
      )}

      <Button variant="primary" onClick={handleSave} disabled={saving}>
        {saving && <Spinner animation="border" size="sm" className="me-2" />}
        Save Changes
      </Button>
    </div>
  );
}

function SiteConfigEditor({ token, onSaved }) {
  const [siteData, setSiteData] = useState(null);
  const [landingPage, setLandingPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPageContent("landing")
      .then((d) => {
        if (!cancelled) {
          setSiteData(d.site ?? {});
          setLandingPage(d);
        }
      })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  function set(field) {
    return (e) => setSiteData((s) => ({ ...s, [field]: e.target.value }));
  }
  function setFooter(field) {
    return (e) => setSiteData((s) => ({ ...s, footer: { ...s.footer, [field]: e.target.value } }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const { site, id, ...page } = landingPage;
      await adminSyncContent(token, {
        ...page,
        site: {
          key: siteData.key ?? "default",
          brand: siteData.brand ?? "PMP",
          navigation: siteData.navigation ?? [],
          footer: siteData.footer ?? {},
        },
      });
      if (onSaved) onSaved();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="py-3 text-center"><Spinner animation="border" size="sm" /></div>;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>{saveError}</Alert>}
      <Form.Group className="mb-3">
        <Form.Label>Brand Name</Form.Label>
        <Form.Control value={tf(siteData?.brand)} onChange={set("brand")} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Footer — Left Text</Form.Label>
        <Form.Control value={tf(siteData?.footer?.lineLeft)} onChange={setFooter("lineLeft")} />
        <Form.Text className="text-muted">Use <code>{"{year}"}</code> for the current year.</Form.Text>
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Label>Footer — Right Text</Form.Label>
        <Form.Control as="textarea" rows={2} value={tf(siteData?.footer?.lineRight)} onChange={setFooter("lineRight")} />
      </Form.Group>
      <Button variant="primary" onClick={handleSave} disabled={saving}>
        {saving && <Spinner animation="border" size="sm" className="me-2" />}
        Save Changes
      </Button>
    </div>
  );
}

const CONTENT_PAGES = [
  { key: "site", label: "Site Config" },
  { key: "landing", label: "Landing" },
  { key: "about", label: "About" },
  { key: "contacts", label: "Contacts" },
];

function ContentSection() {
  const { token } = useAuth();
  const [activePage, setActivePage] = useState("site");
  const [savedKey, setSavedKey] = useState(null);

  function handleSaved() {
    setSavedKey(activePage);
    setTimeout(() => setSavedKey(null), 3000);
  }

  return (
    <div>
      <h5 className="fw-semibold mb-3">Content Manager</h5>
      <Nav variant="underline" activeKey={activePage} onSelect={(k) => setActivePage(k)} className="mb-3 border-bottom">
        {CONTENT_PAGES.map((p) => (
          <Nav.Item key={p.key}>
            <Nav.Link eventKey={p.key} className="px-3 py-2 small">{p.label}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      {savedKey === activePage && (
        <Alert variant="success" className="mb-3">Saved successfully.</Alert>
      )}
      {activePage === "site" ? (
        <SiteConfigEditor token={token} onSaved={handleSaved} />
      ) : (
        <PageContentEditor key={activePage} slug={activePage} token={token} onSaved={handleSaved} />
      )}
    </div>
  );
}

// ── Catalog section ───────────────────────────────────────────────────────────

function FlagBadge({ value }) {
  return <Badge bg={value ? "success" : "secondary"}>{value ? "Yes" : "No"}</Badge>;
}

function emptyItemForm() {
  return {
    sku: "", name: "", description: "", features: "",
    category: "", subCategory: "",
    parts: "", sizes: "", packQty: "", packUnit: "",
    materials: "", imageUrl: "", imageAlt: "",
    available: true, featured: false, onSale: false,
  };
}

// Delimited fields are edited one item per line; the sanitizer (see
// lib/catalogSanitize.js) normalizes them to arrays (features/parts/materials)
// and a ";"-joined description on save.
function itemToForm(item) {
  return {
    sku: item.sku ?? "",
    name: item.name ?? "",
    description: splitDescription(item.description).join("\n"),
    features: (Array.isArray(item.features) ? item.features : []).join("\n"),
    category: item.category ?? "",
    subCategory: item.subCategory ?? "",
    parts: (Array.isArray(item.parts) ? item.parts : []).join("\n"),
    sizes: item.sizes ?? "",
    packQty: item.packQty != null ? String(item.packQty) : "",
    packUnit: item.packUnit ?? "",
    materials: (Array.isArray(item.materials) ? item.materials : []).join("\n"),
    imageUrl: item.imageUrl ?? "",
    imageAlt: item.imageAlt ?? "",
    available: item.available !== false,
    featured: !!item.featured,
    onSale: !!item.onSale,
  };
}

// Build the item payload from the form using already-sanitized delimited fields.
function formToItem(form, san) {
  const packQty = form.packQty.trim();
  const packQtyNum = packQty === "" ? null : Number(packQty);
  return {
    sku: form.sku.trim(),
    name: form.name.trim(),
    description: san.description || "",
    features: san.features,
    category: form.category || null,
    subCategory: form.subCategory || null,
    parts: san.parts,
    sizes: form.sizes.trim() || null,
    packQty: Number.isFinite(packQtyNum) ? packQtyNum : null,
    packUnit: form.packUnit.trim() || null,
    materials: san.materials,
    imageUrl: form.imageUrl.trim() || undefined,
    imageAlt: form.imageAlt.trim() || undefined,
    available: !!form.available,
    featured: !!form.featured,
    onSale: !!form.onSale,
  };
}

function AddCategoryModal({ show, onHide, onCreated, token }) {
  const [form, setForm] = useState({ name: "", summary: "", details: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(field) { return (e) => setForm((f) => ({ ...f, [field]: e.target.value })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await adminCreateCatalogCategory(token, form);
      onCreated(created);
      setForm({ name: "", summary: "", details: "" });
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Add Category</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control value={form.name} onChange={set("name")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Summary</Form.Label>
            <Form.Control value={form.summary} onChange={set("summary")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Details</Form.Label>
            <Form.Control as="textarea" rows={3} value={form.details} onChange={set("details")} required />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}Add
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function EditCategoryModal({ show, target, onHide, onUpdated, token }) {
  const [form, setForm] = useState({ summary: "", details: "" });
  // Each subcategory row tracks its original name (null when newly added) so a
  // rename stays a precise old->new op. Edits are staged here and flushed on Save.
  const [subs, setSubs] = useState([]);
  const [newSub, setNewSub] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (target) {
      setForm({ summary: target.summary ?? "", details: target.details ?? "" });
      setSubs((target.subCategories ?? []).map((s) => ({ orig: s, value: s })));
      setNewSub("");
      setError(null);
    }
  }, [target]);

  function set(field) { return (e) => setForm((f) => ({ ...f, [field]: e.target.value })); }

  function addSub() {
    const name = newSub.trim();
    if (!name) return;
    if (subs.some((s) => s.value.trim().toLowerCase() === name.toLowerCase())) {
      setError(`Subcategory "${name}" already exists`);
      return;
    }
    setSubs((prev) => [...prev, { orig: null, value: name }]);
    setNewSub("");
    setError(null);
  }
  function renameSub(idx, value) {
    setSubs((prev) => prev.map((s, i) => (i === idx ? { ...s, value } : s)));
  }
  function removeSub(idx) {
    setSubs((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const original = target.subCategories ?? [];
      const keptOrigs = subs.filter((s) => s.orig).map((s) => s.orig);
      const removed = original.filter((o) => !keptOrigs.includes(o));
      const renamed = subs.filter((s) => s.orig && s.value.trim() && s.value.trim() !== s.orig);
      const added = subs.filter((s) => s.orig === null && s.value.trim());
      // Deletes first, then renames, then adds, to avoid transient name clashes.
      for (const r of removed) await adminDeleteCatalogSubcategory(token, target.name, r);
      for (const s of renamed) await adminRenameCatalogSubcategory(token, target.name, s.orig, s.value.trim());
      for (const s of added) await adminAddCatalogSubcategory(token, target.name, s.value.trim());
      const updated = await adminUpdateCatalogCategory(token, target.name, form);
      onUpdated(updated);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Edit Category — {target?.name}</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>Summary</Form.Label>
            <Form.Control value={form.summary} onChange={set("summary")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Details</Form.Label>
            <Form.Control as="textarea" rows={3} value={form.details} onChange={set("details")} required />
          </Form.Group>
          <Form.Group className="mb-1">
            <Form.Label>Subcategories</Form.Label>
            <div className="d-flex flex-column gap-2">
              {subs.map((s, idx) => (
                <div key={idx} className="d-flex gap-2 align-items-center">
                  <Form.Control size="sm" value={s.value} onChange={(e) => renameSub(idx, e.target.value)} />
                  <Button variant="link" size="sm" className="p-0 text-danger" title="Remove" onClick={() => removeSub(idx)}>
                    <i className="bi bi-trash" />
                  </Button>
                </div>
              ))}
              {subs.length === 0 && <div className="text-muted small">No subcategories.</div>}
            </div>
            <div className="d-flex gap-2 mt-2">
              <Form.Control
                size="sm"
                placeholder="New subcategory…"
                value={newSub}
                onChange={(e) => setNewSub(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSub(); } }}
              />
              <Button size="sm" variant="outline-secondary" onClick={addSub} disabled={!newSub.trim()}>Add</Button>
            </div>
            <Form.Text className="text-muted">Renames and removals apply to tagged items when you save.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}Save
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function DeleteCategoryModal({ show, target, onHide, onDeleted, token }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await adminDeleteCatalogCategory(token, target.name);
      onDeleted(target.name);
    } catch (err) { setError(err.message); }
    finally { setDeleting(false); }
  }

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Delete Category</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <p>Delete <strong>{target?.name}</strong>?</p>
        {target?.itemCount > 0 && (
          <Alert variant="warning" className="mb-0">
            <strong>{target.itemCount} item{target.itemCount !== 1 ? "s" : ""}</strong> in this category will be unassigned.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={deleting}>Cancel</Button>
        <Button variant="danger" onClick={handleDelete} disabled={deleting}>
          {deleting && <Spinner animation="border" size="sm" className="me-2" />}Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function ItemModal({ show, onHide, onSaved, token, item, seed, categories }) {
  const isEdit = !!item;
  const [form, setForm] = useState(emptyItemForm());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // { name, ext, dataBase64 } for a chosen file; null when none picked.
  const [imageFile, setImageFile] = useState(null);
  const [sanPreview, setSanPreview] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const imgInputRef = useRef(null);

  useEffect(() => {
    if (show) {
      // item => edit; seed => duplicate (create, pre-filled from a source row);
      // neither => blank add.
      setForm(item ? itemToForm(item) : seed ? itemToForm(seed) : emptyItemForm());
      setError(null);
      setImageFile(null);
      setSanPreview(null);
      setImgPreview(null);
      if (imgInputRef.current) imgInputRef.current.value = "";
    }
  }, [show, item, seed]);

  function set(field) { return (e) => setForm((f) => ({ ...f, [field]: e.target.value })); }
  function setCheck(field) { return (e) => setForm((f) => ({ ...f, [field]: e.target.checked })); }
  // Changing category resets the subcategory, which cascades from the category's list.
  function setCategory(e) {
    const category = e.target.value;
    setForm((f) => ({ ...f, category, subCategory: "" }));
  }

  function handleImageFile(e) {
    const file = e.target.files?.[0];
    setImgPreview(null);
    if (!file) { setImageFile(null); return; }
    const ext = imageExtFromName(file.name) || (file.type.split("/")[1] ?? "");
    const reader = new FileReader();
    reader.onload = (ev) => setImageFile({ name: file.name, ext, dataBase64: ev.target.result });
    reader.readAsDataURL(file);
  }

  // Non-destructive: show what the delimited fields will become, without
  // touching the textareas.
  function handlePreviewSanitized() {
    setSanPreview(sanitizeItemFields(form));
  }

  // Compute the {SKU}.{ext} target + destination without uploading anything.
  function handlePreviewImage() {
    const sku = form.sku.trim();
    if (!sku) { setImgPreview({ error: "Enter a SKU first." }); return; }
    if (imageFile) {
      if (!imageFile.ext) { setImgPreview({ error: "Couldn't determine the file extension." }); return; }
      setImgPreview({ ...imageDestination(sku, imageFile.ext, API_BASE), source: `upload: ${imageFile.name}` });
      return;
    }
    const url = form.imageUrl.trim();
    if (!url) { setImgPreview({ error: "Choose a file or enter an image URL." }); return; }
    if (/^\/assets\//.test(url)) {
      setImgPreview({ passthrough: url, source: "existing local path (left as-is, no upload)" });
      return;
    }
    const ext = imageExtFromUrl(url);
    if (!ext) { setImgPreview({ error: "Couldn't determine the extension from the URL." }); return; }
    setImgPreview({ ...imageDestination(sku, ext, API_BASE), source: `url: ${url}` });
  }

  const selectedCat = categories.find((c) => c.name === form.category);
  const subOptions = selectedCat?.subCategories ?? [];
  // Preserve a legacy/imported subCategory that isn't in the authored list yet.
  const showLegacySub = form.subCategory && !subOptions.includes(form.subCategory);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    // Re-run sanitation for real and block on any rejection.
    const san = sanitizeItemFields(form);
    if (san.errors.length) {
      setSanPreview(san);
      setError("Fix the highlighted field errors before saving.");
      return;
    }
    setSaving(true);
    try {
      const sku = form.sku.trim();
      // Resolve the image: an uploaded file wins; a remote URL is fetched &
      // persisted server-side; an existing /assets path is left untouched.
      let imageUrl = form.imageUrl.trim();
      if (imageFile) {
        const up = await adminUploadCatalogImage(token, { sku, dataBase64: imageFile.dataBase64, ext: imageFile.ext });
        imageUrl = up.imageUrl;
      } else if (/^https?:\/\//i.test(imageUrl)) {
        const up = await adminUploadCatalogImage(token, { sku, sourceUrl: imageUrl });
        imageUrl = up.imageUrl;
      }
      const payload = { ...formToItem(form, san), imageUrl: imageUrl || undefined };
      if (isEdit) {
        await adminUpdateCatalogItem(token, item.sku, payload);
      } else {
        await adminCreateCatalogItem(token, payload);
      }
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton><Modal.Title>{isEdit ? "Edit Item" : "Add Item"}</Modal.Title></Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label>SKU <span className="text-danger">*</span></Form.Label>
            <Form.Control value={form.sku} onChange={set("sku")} required readOnly={isEdit} plaintext={isEdit} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Name <span className="text-danger">*</span></Form.Label>
            <Form.Control value={form.name} onChange={set("name")} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={3} value={form.description} onChange={set("description")} placeholder={"One paragraph per line.\nLists aren't allowed here — use Features."} />
            <Form.Text className="text-muted">One paragraph per line (saved as <code>;</code>-joined). Bulleted/numbered lists are rejected.</Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Features</Form.Label>
            <Form.Control as="textarea" rows={3} value={form.features} onChange={set("features")} placeholder={"One feature per line.\nPaste bulleted/numbered/dashed lists — markers are stripped."} />
            <Form.Text className="text-muted">One per line; saved as <code>;</code>-joined.</Form.Text>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select value={form.category} onChange={setCategory}>
                  <option value="">— No Category —</option>
                  {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Subcategory</Form.Label>
                <Form.Select value={form.subCategory} onChange={set("subCategory")} disabled={!form.category}>
                  <option value="">— None —</option>
                  {subOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                  {showLegacySub && <option value={form.subCategory}>{form.subCategory} (legacy)</option>}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Parts (SKUs)</Form.Label>
                <Form.Control as="textarea" rows={2} value={form.parts} onChange={set("parts")} placeholder={"One SKU per line"} />
                <Form.Text className="text-muted">One per line; saved as <code>|</code>-joined.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sizes</Form.Label>
                <Form.Control value={form.sizes} onChange={set("sizes")} placeholder='1 gal, 2.8-4.2mm' />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Pack Qty</Form.Label>
                <Form.Control type="number" min="0" value={form.packQty} onChange={set("packQty")} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Pack Unit</Form.Label>
                <Form.Control value={form.packUnit} onChange={set("packUnit")} placeholder="box, pkg, case" />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Materials</Form.Label>
            <Form.Control as="textarea" rows={2} value={form.materials} onChange={set("materials")} placeholder={"One material per line"} />
            <Form.Text className="text-muted">One per line; saved as <code>|</code>-joined.</Form.Text>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Image URL</Form.Label>
                <Form.Control value={form.imageUrl} onChange={set("imageUrl")} placeholder="https://… or /assets/products/…" disabled={!!imageFile} />
                <Form.Text className="text-muted">A remote URL is downloaded and saved as <code>{"{SKU}"}.{"{ext}"}</code> on save.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Image Alt Text</Form.Label>
                <Form.Control value={form.imageAlt} onChange={set("imageAlt")} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Upload Image</Form.Label>
            <Form.Control type="file" accept="image/*" ref={imgInputRef} onChange={handleImageFile} />
            {imageFile && (
              <Form.Text className="text-muted d-block">
                Selected: {imageFile.name} — uploads as <code>{form.sku.trim() || "{SKU}"}.{imageFile.ext || "?"}</code> on save (overrides the URL field).
              </Form.Text>
            )}
          </Form.Group>
          <div className="d-flex gap-4 mb-3">
            <Form.Check type="checkbox" id="item-available" label="Available" checked={form.available} onChange={setCheck("available")} />
            <Form.Check type="checkbox" id="item-featured" label="Featured" checked={form.featured} onChange={setCheck("featured")} />
            <Form.Check type="checkbox" id="item-onsale" label="On Sale" checked={form.onSale} onChange={setCheck("onSale")} />
          </div>

          {/* ── Test tools ── */}
          <div className="d-flex gap-2 flex-wrap border-top pt-3">
            <Button type="button" variant="outline-secondary" size="sm" onClick={handlePreviewSanitized}>
              Preview Sanitized
            </Button>
            <Button type="button" variant="outline-secondary" size="sm" onClick={handlePreviewImage}>
              Preview Image Target
            </Button>
          </div>

          {sanPreview && (
            <div className="mt-3 p-3 rounded bg-light border small">
              <div className="d-flex justify-content-between mb-2">
                <strong>Sanitized values</strong>
                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setSanPreview(null)}>dismiss</Button>
              </div>
              {sanPreview.errors.length > 0 && (
                <Alert variant="danger" className="py-2 mb-2">
                  <ul className="mb-0 ps-3">
                    {sanPreview.errors.map((er, i) => (
                      <li key={i}><strong>{er.field}:</strong> {er.message}</li>
                    ))}
                  </ul>
                </Alert>
              )}
              <dl className="row mb-0">
                {["Description", "Features", "Parts", "Materials"].map((k) => (
                  <Fragment key={k}>
                    <dt className="col-sm-3 text-muted fw-normal">{k}</dt>
                    <dd className="col-sm-9 mb-1 font-monospace text-break">{sanPreview.preview[k]}</dd>
                  </Fragment>
                ))}
              </dl>
            </div>
          )}

          {imgPreview && (
            <div className="mt-3 p-3 rounded bg-light border small">
              <div className="d-flex justify-content-between mb-2">
                <strong>Image target</strong>
                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setImgPreview(null)}>dismiss</Button>
              </div>
              {imgPreview.error ? (
                <span className="text-danger">{imgPreview.error}</span>
              ) : imgPreview.passthrough ? (
                <div>
                  <div className="text-muted">{imgPreview.source}</div>
                  <div className="font-monospace text-break">{imgPreview.passthrough}</div>
                </div>
              ) : (
                <dl className="row mb-0">
                  <dt className="col-sm-3 text-muted fw-normal">Source</dt>
                  <dd className="col-sm-9 mb-1 text-break">{imgPreview.source}</dd>
                  <dt className="col-sm-3 text-muted fw-normal">File name</dt>
                  <dd className="col-sm-9 mb-1 font-monospace">{imgPreview.fileName}</dd>
                  <dt className="col-sm-3 text-muted fw-normal">Destination</dt>
                  <dd className="col-sm-9 mb-1 font-monospace text-break">{imgPreview.diskPath}</dd>
                  <dt className="col-sm-3 text-muted fw-normal">imageUrl</dt>
                  <dd className="col-sm-9 mb-0 font-monospace text-break">{imgPreview.imageUrl}</dd>
                </dl>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving && <Spinner animation="border" size="sm" className="me-2" />}
            {isEdit ? "Save" : "Add Item"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

function CatalogSection() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("categories");

  // ── Categories state ──────────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [catsLoading, setCatsLoading] = useState(true);
  const [catsError, setCatsError] = useState(null);
  const [showAddCat, setShowAddCat] = useState(false);
  const [editCatTarget, setEditCatTarget] = useState(null);
  const [deleteCatTarget, setDeleteCatTarget] = useState(null);

  // ── Items state ───────────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("none");
  const [skuSearch, setSkuSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [showAddItem, setShowAddItem] = useState(false);
  const [editItemTarget, setEditItemTarget] = useState(null);
  const [duplicateSeed, setDuplicateSeed] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const [bulkCatAssign, setBulkCatAssign] = useState("");
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [importParsed, setImportParsed] = useState(null);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadCategories(); }, []);

  useEffect(() => {
    if (activeTab === "items") loadItems();
  }, [activeTab, categoryFilter]);

  async function loadCategories() {
    setCatsLoading(true);
    setCatsError(null);
    try {
      const res = await adminFetchCatalogCategories(token);
      setCategories(res.categories ?? []);
      setUnassignedCount(res.unassignedCount ?? 0);
    }
    catch (err) { setCatsError(err.message); }
    finally { setCatsLoading(false); }
  }

  async function loadItems() {
    setItemsLoading(true);
    setItemsError(null);
    setSelected(new Set());
    try {
      const params = categoryFilter ? { category: categoryFilter } : {};
      setItems(await adminFetchAllCatalogItems(token, params));
    }
    catch (err) { setItemsError(err.message); }
    finally { setItemsLoading(false); }
  }

  function handleCatCreated(cat) { setCategories((prev) => [...prev, cat]); setShowAddCat(false); }
  function handleCatUpdated(updated) {
    setCategories((prev) => prev.map((c) => c.name === updated.name ? { ...c, ...updated } : c));
    setEditCatTarget(null);
  }
  function handleCatDeleted(name) {
    setCategories((prev) => prev.filter((c) => c.name !== name));
    setDeleteCatTarget(null);
    if (activeTab === "items") loadItems();
  }

  function handleItemSaved() { setShowAddItem(false); setEditItemTarget(null); setDuplicateSeed(null); loadItems(); }

  // First free "{SKU}-COPY" / "-COPY-2"… among the loaded (category-filtered)
  // items; best-effort only — the server's unique index is authoritative.
  function suggestCopySku(baseSku) {
    const existing = new Set(items.map((i) => i.sku));
    let candidate = `${baseSku}-COPY`;
    let n = 2;
    while (existing.has(candidate)) candidate = `${baseSku}-COPY-${n++}`;
    return candidate;
  }

  // Open Add Item pre-filled from a row, with a fresh SKU and no image.
  function handleDuplicateItem(item) {
    setDuplicateSeed({ ...item, sku: suggestCopySku(item.sku), imageUrl: "" });
  }

  function toggleSelect(sku) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(sku) ? next.delete(sku) : next.add(sku);
      return next;
    });
  }

  const displayedItems = skuSearch
    ? items.filter((i) => i.sku.toLowerCase().includes(skuSearch.toLowerCase()))
    : items;

  function toggleSelectAll() {
    if (selected.size === displayedItems.length && displayedItems.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(displayedItems.map((i) => i.sku)));
    }
  }

  function csvEscape(val) {
    const s = val == null ? "" : String(val);
    return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  }

  async function handleExportCsv() {
    try {
      const all = await adminFetchAllCatalogItems(token, {});
      // Column order/headers mirror _latest.csv so export round-trips with import.
      const cols = ["sku", "name", "description", "Features", "category", "sub_category", "parts", "sizes", "packQty", "packUnit", "materials", "imageUrl", "imageAlt", "available", "featured", "onSale", "createdAt", "lastModifiedAt"];
      const rows = [cols.join(",")];
      for (const item of all) {
        rows.push(cols.map((c) => {
          if (c === "Features") return csvEscape(Array.isArray(item.features) ? item.features.join(";") : (item.features ?? ""));
          if (c === "sub_category") return csvEscape(item.subCategory ?? "");
          if (c === "parts") return csvEscape(Array.isArray(item.parts) ? item.parts.join("|") : (item.parts ?? ""));
          if (c === "materials") return csvEscape(Array.isArray(item.materials) ? item.materials.join("|") : (item.materials ?? ""));
          return csvEscape(item[c]);
        }).join(","));
      }
      const blob = new Blob([rows.join("\n")], { type: "text/csv" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "catalog.csv";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      alert("Export failed: " + err.message);
    }
  }

  function parseCsv(text) {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length < 2) return [];
    const headers = lines[0].split(",");
    return lines.slice(1).map((line) => {
      const vals = [];
      let cur = "", inQuote = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuote) {
          if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (ch === '"') inQuote = false;
          else cur += ch;
        } else if (ch === '"') { inQuote = true; }
        else if (ch === ",") { vals.push(cur); cur = ""; }
        else cur += ch;
      }
      vals.push(cur);
      const obj = {};
      headers.forEach((h, i) => { obj[h.trim()] = vals[i] ?? ""; });
      // Normalize CSV headers to the canonical camelCase schema (see seedCatalog.js):
      // Features split on ';'; materials/parts on '|'; packQty numeric; sub_category -> subCategory.
      const splitPipe = (s) => (s ? String(s).split("|").map((x) => x.trim()).filter(Boolean) : []);
      const splitSemi = (s) => (s ? String(s).split(";").map((x) => x.trim()).filter(Boolean) : []);
      const packQtyRaw = (obj.packQty ?? "").trim();
      const packQtyNum = packQtyRaw === "" ? null : Number(packQtyRaw);
      const norm = {
        sku: (obj.sku ?? "").trim(),
        name: obj.name ?? "",
        description: obj.description ?? "",
        features: splitSemi(obj.Features ?? obj.features),
        category: (obj.category ?? "").trim() || null,
        subCategory: (obj.sub_category ?? obj.subCategory ?? "").trim() || null,
        parts: splitPipe(obj.parts),
        sizes: (obj.sizes ?? "").trim() || null,
        packQty: Number.isFinite(packQtyNum) ? packQtyNum : null,
        packUnit: (obj.packUnit ?? "").trim() || null,
        materials: splitPipe(obj.materials),
        imageUrl: (obj.imageUrl ?? "").trim() || undefined,
        imageAlt: (obj.imageAlt ?? "").trim() || undefined,
      };
      if ("available" in obj) norm.available = /^true$/i.test(String(obj.available).trim());
      if ("featured" in obj) norm.featured = /^true$/i.test(String(obj.featured).trim());
      if ("onSale" in obj) norm.onSale = /^true$/i.test(String(obj.onSale).trim());
      return norm;
    }).filter((o) => o.sku);
  }

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCsv(ev.target.result);
      setImportParsed(parsed);
      setShowImportConfirm(true);
    };
    reader.readAsText(file);
  }

  async function handleImportConfirm() {
    setImportError(null);
    setImportResult(null);
    setShowImportConfirm(false);
    try {
      const result = await adminImportCatalog(token, importParsed);
      setImportResult(`Import complete: ${result.inserted} inserted, ${result.updated} updated.`);
      loadItems();
    } catch (err) {
      setImportError("Import failed: " + err.message);
    }
    setImportParsed(null);
  }

  async function handleBulkOp(op, value) {
    setBulkError(null);
    const skus = Array.from(selected);
    try {
      await adminBulkCatalogItems(token, { skus, op, value });
      setSelected(new Set());
      loadItems();
    } catch (err) { setBulkError(err.message); }
  }

  async function handleDeleteItem(sku) {
    try { await adminDeleteCatalogItem(token, sku); loadItems(); }
    catch (err) { alert(err.message); }
  }

  const selectedSkus = Array.from(selected);

  return (
    <div>
      <h5 className="fw-semibold mb-4">Catalog Manager</h5>

      <Nav variant="tabs" className="mb-4" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
        <Nav.Item><Nav.Link eventKey="categories">Categories</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="items">Items</Nav.Link></Nav.Item>
      </Nav>

      {/* ── Categories tab ── */}
      {activeTab === "categories" && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="text-muted small">{categories.length} categor{categories.length !== 1 ? "ies" : "y"}</span>
            <Button variant="primary" size="sm" onClick={() => setShowAddCat(true)}>+ Add Category</Button>
          </div>
          {catsError && <Alert variant="danger">{catsError}</Alert>}
          {catsLoading ? (
            <div className="py-4 text-center"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Summary</th>
                    <th className="text-center">Items</th>
                    <th className="text-center">Subcats</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((c) => (
                    <tr key={c.name}>
                      <td className="fw-semibold">{c.name}</td>
                      <td className="text-muted small">{c.summary}</td>
                      <td className="text-center">{c.itemCount ?? 0}</td>
                      <td className="text-center">{c.subCategories?.length ?? 0}</td>
                      <td className="text-end">
                        <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => setEditCatTarget(c)}>Edit</Button>
                        <Button variant="outline-danger" size="sm" onClick={() => setDeleteCatTarget(c)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                  {categories.length === 0 && (
                    <tr><td colSpan={5} className="text-center text-muted py-4">No categories yet.</td></tr>
                  )}
                  {/* Virtual triage bucket for items with no category — manager-only. */}
                  <tr className="text-muted fst-italic">
                    <td>Unassigned</td>
                    <td className="small">Items with no category (visible only here)</td>
                    <td className="text-center">{unassignedCount}</td>
                    <td className="text-center">—</td>
                    <td className="text-end">—</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}

          <AddCategoryModal show={showAddCat} onHide={() => setShowAddCat(false)} onCreated={handleCatCreated} token={token} />
          <EditCategoryModal show={!!editCatTarget} target={editCatTarget} onHide={() => setEditCatTarget(null)} onUpdated={handleCatUpdated} token={token} />
          <DeleteCategoryModal show={!!deleteCatTarget} target={deleteCatTarget} onHide={() => setDeleteCatTarget(null)} onDeleted={handleCatDeleted} token={token} />
        </div>
      )}

      {/* ── Items tab ── */}
      {activeTab === "items" && (
        <div>
          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
            <div className="d-flex gap-2 flex-wrap">
              <Form.Select size="sm" style={{ maxWidth: 200 }} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="none">Unassigned</option>
                {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
              </Form.Select>
              <Form.Control
                size="sm" style={{ maxWidth: 180 }}
                placeholder="Search SKU…"
                value={skuSearch}
                onChange={(e) => setSkuSearch(e.target.value)}
              />
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={handleExportCsv}>Export CSV</Button>
              <Button variant="outline-secondary" size="sm" onClick={() => fileInputRef.current?.click()}>Import CSV</Button>
              <input type="file" accept=".csv" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileSelect} />
              <Button variant="primary" size="sm" onClick={() => setShowAddItem(true)}>+ Add Item</Button>
            </div>
          </div>

          {importResult && <Alert variant="success" dismissible onClose={() => setImportResult(null)}>{importResult}</Alert>}
          {importError && <Alert variant="danger" dismissible onClose={() => setImportError(null)}>{importError}</Alert>}

          {bulkError && <Alert variant="danger" dismissible onClose={() => setBulkError(null)}>{bulkError}</Alert>}

          {selectedSkus.length > 0 && (
            <div className="d-flex flex-wrap gap-2 align-items-center p-2 mb-3 rounded border bg-light">
              <span className="small fw-semibold me-1">{selectedSkus.length} selected:</span>
              <div className="d-flex align-items-center gap-1">
                <Form.Select size="sm" style={{ width: 160 }} value={bulkCatAssign} onChange={(e) => setBulkCatAssign(e.target.value)}>
                  <option value="">Assign Category…</option>
                  <option value="__none__">Unassign</option>
                  {categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                </Form.Select>
                <Button size="sm" variant="outline-secondary" disabled={!bulkCatAssign}
                  onClick={() => { handleBulkOp("setCategory", bulkCatAssign === "__none__" ? null : bulkCatAssign); setBulkCatAssign(""); }}>
                  Apply
                </Button>
              </div>
              <Button size="sm" variant="outline-success" onClick={() => handleBulkOp("setAvailable", true)}>Available On</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => handleBulkOp("setAvailable", false)}>Available Off</Button>
              <Button size="sm" variant="outline-warning" onClick={() => handleBulkOp("setFeatured", true)}>Featured On</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => handleBulkOp("setFeatured", false)}>Featured Off</Button>
              <Button size="sm" variant="outline-info" onClick={() => handleBulkOp("setOnSale", true)}>On Sale On</Button>
              <Button size="sm" variant="outline-secondary" onClick={() => handleBulkOp("setOnSale", false)}>On Sale Off</Button>
              <Button size="sm" variant="outline-danger" onClick={() => setShowBulkDeleteConfirm(true)}>Delete Selected</Button>
            </div>
          )}

          {itemsError && <Alert variant="danger">{itemsError}</Alert>}
          {itemsLoading ? (
            <div className="py-4 text-center"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 32 }}>
                      <Form.Check
                        type="checkbox"
                        checked={displayedItems.length > 0 && displayedItems.every((i) => selected.has(i.sku))}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>SKU</th>
                    <th>Name</th>
                    <th>Category</th>
                    <th className="text-center">Avail.</th>
                    <th className="text-center">Featured</th>
                    <th className="text-center">On Sale</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedItems.map((item) => (
                    <tr key={item.sku}>
                      <td>
                        <Form.Check type="checkbox" checked={selected.has(item.sku)} onChange={() => toggleSelect(item.sku)} />
                      </td>
                      <td className="font-monospace text-muted">{item.sku}</td>
                      <td className="fw-semibold">{item.name}</td>
                      <td>{item.category ? <Badge bg="primary" className="fw-normal">{item.category}</Badge> : <span className="text-muted">—</span>}</td>
                      <td className="text-center"><FlagBadge value={item.available !== false} /></td>
                      <td className="text-center"><FlagBadge value={!!item.featured} /></td>
                      <td className="text-center"><FlagBadge value={!!item.onSale} /></td>
                      <td className="text-end">
                        <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => setEditItemTarget(item)}>Edit</Button>
                        <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => handleDuplicateItem(item)}>Duplicate</Button>
                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteItem(item.sku)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                  {displayedItems.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-muted py-4">No items found.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}

          <ItemModal show={showAddItem} onHide={() => setShowAddItem(false)} onSaved={handleItemSaved} token={token} item={null} categories={categories} />
          <ItemModal show={!!editItemTarget} onHide={() => setEditItemTarget(null)} onSaved={handleItemSaved} token={token} item={editItemTarget} categories={categories} />
          <ItemModal show={!!duplicateSeed} onHide={() => setDuplicateSeed(null)} onSaved={handleItemSaved} token={token} item={null} seed={duplicateSeed} categories={categories} />

          <Modal show={showBulkDeleteConfirm} onHide={() => setShowBulkDeleteConfirm(false)} centered>
            <Modal.Header closeButton><Modal.Title>Delete Items</Modal.Title></Modal.Header>
            <Modal.Body>Delete <strong>{selectedSkus.length}</strong> selected item{selectedSkus.length !== 1 ? "s" : ""}? This cannot be undone.</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowBulkDeleteConfirm(false)}>Cancel</Button>
              <Button variant="danger" onClick={() => { setShowBulkDeleteConfirm(false); handleBulkOp("delete"); }}>Delete</Button>
            </Modal.Footer>
          </Modal>

          <Modal show={showImportConfirm} onHide={() => setShowImportConfirm(false)} centered>
            <Modal.Header closeButton><Modal.Title>Import Catalog CSV</Modal.Title></Modal.Header>
            <Modal.Body>
              <Alert variant="warning" className="mb-3">
                <strong>Warning:</strong> Importing will update all matching SKUs and add new ones. Existing item data will be overwritten. An Export CSV backup is recommended before proceeding.
              </Alert>
              <p className="mb-0">{importParsed?.length ?? 0} item row{importParsed?.length !== 1 ? "s" : ""} found in the file.</p>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowImportConfirm(false)}>Cancel</Button>
              <Button variant="danger" onClick={handleImportConfirm}>Import</Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </div>
  );
}

// ── Main ProfilePage ──────────────────────────────────────────────────────────
export default function ProfilePage() {
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState("general");

  const navItems = [
    { key: "general", label: "General" },
    { key: "account", label: "Account" },
    { key: "notifications", label: "Notifications" },
    ...(user?.role === "admin" ? [
      { key: "admin", label: "Admin" },
      { key: "content", label: "Content" },
      { key: "catalog", label: "Catalog" },
    ] : []),
  ];

  function renderSection() {
    switch (activeSection) {
      case "general": return <GeneralSection />;
      case "account": return <AccountSection />;
      case "notifications": return <NotificationsSection />;
      case "admin": return user?.role === "admin" ? <AdminSection /> : null;
      case "content": return user?.role === "admin" ? <ContentSection /> : null;
      case "catalog": return user?.role === "admin" ? <CatalogSection /> : null;
      default: return null;
    }
  }

  return (
    <Container className="py-5">
      <h2 className="fw-bold text-primary mb-3">My Profile</h2>
      <Nav
        variant="underline"
        activeKey={activeSection}
        onSelect={(k) => setActiveSection(k)}
        className="mb-0 border-bottom"
      >
        {navItems.map((item) => (
          <Nav.Item key={item.key}>
            <Nav.Link eventKey={item.key} className="px-3 py-2">
              {item.label}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      <Card className="shadow-sm border-0 rounded-top-0">
        <Card.Body className="p-4">
          {renderSection()}
        </Card.Body>
      </Card>
    </Container>
  );
}
